/**
 * Small formatting helpers shared by the editor dashboard screens
 * (`EditorScreen.tsx`, `EditorHub.tsx`).
 *
 * @module web/screens/editorFormat
 */
import type { EditorCard } from "../api/types.ts";
import type { I18n } from "../i18n/index.tsx";
import type { EditorProfile } from "./editorProfile.ts";

/**
 * The in-app card details page for a work item (spec #316): `/editor/card/<id>`,
 * keeping a side-by-side `/next` prefix so the link stays on the same build -
 * the inverse of `editorCardIdFromPath` in App.tsx.
 */
export function editorCardHref(workItemId: string, pathname: string = window.location.pathname): string {
  const prefix = /^(.*?)\/editor(?:\/|$)/.exec(pathname)?.[1] ?? "";
  return `${prefix}/editor/card/${encodeURIComponent(workItemId)}`;
}

/** Up to two initials from a brand name, for the offer avatar. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0];
  if (!first) return "?";
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1] ?? first;
  return (first.charAt(0) + last.charAt(0)).toUpperCase();
}


/** The card's required language (from its spec), falling back to the editor's. */
export function cardLanguage(card: EditorCard, me: EditorProfile): string | null {
  const specLang = card.deliverableSpec.language;
  const l = typeof specLang === "string" && specLang ? specLang : me.languages[0];
  return l ? l.slice(0, 2).toUpperCase() : null;
}


/**
 * Whole minutes of upload grace left once a card's deadline has passed, null when
 * the deadline is still ahead or the grace is over. The grace itself is the server's
 * (`graceEndsAt`, from `payroll-engine.ts#LATE_GRACE_MINUTES`).
 */
export function graceMinutesLeft(card: { deadlineAt?: string | null | undefined; graceEndsAt?: string | null | undefined }, nowMs: number = Date.now()): number | null {
  if (!card.deadlineAt || !card.graceEndsAt || new Date(card.deadlineAt).getTime() > nowMs) return null;
  const left = new Date(card.graceEndsAt).getTime() - nowMs;
  return left > 0 ? Math.ceil(left / 60_000) : null;
}

/** "in 3h", "in 2d", "5h ago" ("in 3 Std.", "vor 2 Tagen") - coarse and timezone-free on purpose. */
export function relative(iso: string, t: I18n["t"]): string {
  const ms = new Date(iso).getTime() - Date.now();
  const future = ms >= 0;
  const h = Math.round(Math.abs(ms) / 3600000);
  if (h < 1) return t(future ? "common.inUnderHour" : "common.underHourAgo");
  if (h < 48) return t(future ? "common.inHours" : "common.hoursAgo", { n: h });
  return t(future ? "common.inDays" : "common.daysAgo", { n: Math.round(h / 24) });
}


export const EN_MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/**
 * The server's period label ("October 2026", always English) in the editor's
 * language ("Oktober 2026"); anything not shaped "<Month> <year>" passes
 * through unchanged rather than guessing.
 */
export function periodLabel(label: string, { date }: I18n): string {
  const m = /^([A-Za-z]+) (\d{4})$/.exec(label.trim());
  const month = m ? EN_MONTHS.indexOf(m[1] ?? "") : -1;
  if (!m || month < 0) return label;
  return date(new Date(Date.UTC(Number(m[2]), month, 1)), { month: "long", year: "numeric", timeZone: "UTC" });
}

